import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, DELIVERY_STATUSES, POD_STATUSES, labelOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusBadge, PodBadge } from "@/components/StatusBadge";
import { API } from "@/lib/api";
import { MessageSquare, Send, Pencil, Trash2, RefreshCw, History, X, Upload } from "lucide-react";

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function DispatchDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [comms, setComms] = useState([]);
  const [client, setClient] = useState(null);
  const [transporter, setTransporter] = useState(null);
  const [queryOpen, setQueryOpen] = useState(false);
  const [qForm, setQForm] = useState({ category: "dispatch", subject: "", message: "" });
  const [resendOpen, setResendOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selContacts, setSelContacts] = useState({});
  const [selChannels, setSelChannels] = useState({ whatsapp: true, email: true });
  const [selDocs, setSelDocs] = useState({});
  const [resending, setResending] = useState(false);
  const [lastEmail, setLastEmail] = useState(null);
  const [transporters, setTransporters] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const isClient = user?.role === "client";
  const canEdit = ["super_admin", "operations", "sales", "accounts"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/dispatches/${id}`);
      setD(data);
      const [docs, comm, c, t, tAll] = await Promise.all([
        api.get(`/dispatches/${id}/documents`).catch(() => ({ data: [] })),
        api.get(`/communications?dispatch_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/clients/${data.client_id}`).catch(() => ({ data: null })),
        api.get(`/transporters`).then((r) => r.data.find((x) => x.id === data.transporter_id)).catch(() => null),
        api.get(`/transporters`).catch(() => ({ data: [] })),
      ]);
      setDocuments(docs.data || []); setComms(comm.data || []); setClient(c.data || null); setTransporter(t || null);
      setTransporters(tAll.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, [id]);

  const updateField = async (field, value) => {
    try {
      await api.patch(`/dispatches/${id}`, { [field]: value });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = () => {
    setEditForm({
      invoice_number: d.invoice_number || "",
      invoice_date: (d.invoice_date || "").slice(0, 10),
      invoice_amount: d.invoice_amount ?? "",
      client_po_number: d.client_po_number || "",
      po_date: (d.po_date || "").slice(0, 10),
      delivery_location: d.delivery_location || "",
      transporter_id: d.transporter_id || "",
      dispatch_date: (d.dispatch_date || "").slice(0, 10),
      lr_number: d.lr_number || "",
      transport_amount: d.transport_amount ?? 0,
      loading_cost: d.loading_cost ?? 0,
      unloading_cost: d.unloading_cost ?? 0,
      packing_cost: d.packing_cost ?? 0,
      insurance_cost: d.insurance_cost ?? 0,
      other_charges: d.other_charges ?? 0,
      expected_delivery: (d.expected_delivery || "").slice(0, 10),
      remarks: d.remarks || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      // Coerce numeric fields
      const payload = { ...editForm };
      ["invoice_amount", "transport_amount", "loading_cost", "unloading_cost", "packing_cost", "insurance_cost", "other_charges"].forEach((k) => {
        if (payload[k] !== "" && payload[k] != null) payload[k] = Number(payload[k]);
      });
      // Strip empty strings so backend keeps existing values
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.patch(`/dispatches/${id}`, payload);
      toast.success("Dispatch updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Remove "${doc.original_filename}"? This will hide it from clients and notifications.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const replaceDoc = async (doc, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/documents/${doc.id}/replace`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc.doc_type} replaced with ${file.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async () => {
    setHistoryOpen(true); setHistoryLoading(true);
    try {
      const { data } = await api.get(`/dispatches/${id}/audit-logs`);
      setHistoryLogs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistoryLoading(false); }
  };

  const uploadDocForSlot = async (docType, file, isClientVisible = true) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    form.append("is_client_visible", isClientVisible ? "true" : "false");
    try {
      await api.post(`/dispatches/${id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType} uploaded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!d) return <div className="text-slate-500">Loading…</div>;

  const trackingUrl = `${window.location.origin}/track/${d.tracking_token}`;

  const submitQuery = async () => {
    if (!qForm.subject.trim() || !qForm.message.trim()) return toast.error("Subject and message required");
    try {
      const { data } = await api.post("/queries", { dispatch_id: id, ...qForm });
      toast.success("Query raised — SRAD team will respond");
      setQueryOpen(false);
      setQForm({ category: "dispatch", subject: "", message: "" });
      nav(`/queries`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openResend = async () => {
    try {
      const [{ data: cts }, { data: le }] = await Promise.all([
        api.get(`/contacts?client_id=${d.client_id}`),
        api.get(`/dispatches/${id}/last-email`).catch(() => ({ data: {} })),
      ]);
      // Duplicate-send warning if a previous successful email exists
      if (le && le.status === "sent" && le.sent_at) {
        const when = new Date(le.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
        const count = le.recipient_count || (le.to_list ? le.to_list.length : 1);
        const ok = window.confirm(
          `Dispatch notification was already sent on ${when} to ${count} recipient(s).\n\nDo you still want to resend?`
        );
        if (!ok) return;
        setLastEmail(le);
      } else {
        setLastEmail(null);
      }
      setContacts(cts);
      const s = {}; cts.filter((c) => c.is_active !== false).forEach((c) => s[c.id] = true);
      setSelContacts(s);
      // Auto-pre-tick Invoice, LR and POD documents
      const autoTypes = ["Tax Invoice", "LR / Lorry Receipt", "POD"];
      const sd = {}; documents.forEach((doc) => sd[doc.id] = autoTypes.includes(doc.doc_type));
      setSelDocs(sd);
      setSelChannels({ whatsapp: true, email: true });
      setResendOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doResend = async () => {
    const contact_ids = Object.entries(selContacts).filter(([, v]) => v).map(([k]) => k);
    const channels = Object.entries(selChannels).filter(([, v]) => v).map(([k]) => k);
    const attach_document_ids = Object.entries(selDocs).filter(([, v]) => v).map(([k]) => k);
    if (contact_ids.length === 0 || channels.length === 0) return toast.error("Pick at least one contact and one channel");
    setResending(true);
    try {
      const { data } = await api.post("/notify/send", { dispatch_id: id, contact_ids, channels, attach_document_ids });
      toast.success(`Resent · Sent ${data.summary.sent} / Failed ${data.summary.failed}`);
      setResendOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResending(false); }
  };

  return (
    <div data-testid="page-dispatch-detail" className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono">{d.dispatch_code}</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Invoice {d.invoice_number}</h1>
          <div className="text-sm text-slate-500 mt-1">{client?.name} · {transporter?.name} · LR {d.lr_number}</div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <button
                data-testid="btn-edit-dispatch"
                onClick={openEdit}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1"
              ><Pencil size={13} /> Edit</button>
              <button
                data-testid="btn-view-history"
                onClick={openHistory}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1"
              ><History size={13} /> History</button>
            </>
          )}
          {!isClient && (
            <button
              data-testid="btn-resend"
              onClick={openResend}
              className="bg-[var(--amber)] hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1"
            ><Send size={13} /> Resend Notifications</button>
          )}
          <button
            data-testid="btn-raise-query"
            onClick={() => setQueryOpen(true)}
            className="bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1"
          ><MessageSquare size={13} /> Raise Query</button>
          <StatusBadge status={d.delivery_status} />
          <PodBadge status={d.pod_status} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric label="Invoice Value" value={fmtInr(d.invoice_amount)} />
        <Metric label="Transport Cost" value={fmtInr(d.total_logistics_cost)} />
        <Metric label="Expected Delivery" value={d.expected_delivery || "-"} />
        <Metric label="Actual Delivery" value={d.actual_delivery || "-"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {!isClient && (
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h2 className="font-heading text-lg font-bold mb-3">Update Status</h2>
            <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Delivery Status</label>
              <select data-testid="select-delivery-status" value={d.delivery_status} onChange={(e) => updateField("delivery_status", e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                {DELIVERY_STATUSES.map((s) => <option key={s} value={s}>{labelOf(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">POD Status</label>
              <select value={d.pod_status} onChange={(e) => updateField("pod_status", e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                {POD_STATUSES.map((s) => <option key={s} value={s}>{labelOf(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Actual Delivery Date</label>
              <input type="date" value={d.actual_delivery || ""} onChange={(e) => updateField("actual_delivery", e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
          </div>
        </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="font-heading text-lg font-bold mb-3">Secure Tracking Link</h2>
          <div className="text-xs text-slate-500 mb-2">Share this link with clients. Only whitelisted documents are exposed.</div>
          <div className="flex items-center gap-2">
            <input readOnly value={trackingUrl} className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-xs font-mono bg-slate-50" />
            <button data-testid="btn-copy-tracking" onClick={() => { navigator.clipboard.writeText(trackingUrl); toast.success("Copied"); }} className="text-sm bg-[var(--navy)] text-white px-3 py-2 rounded-md">Copy</button>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 mb-2">Combine every attached PDF/image into a single <b>Dispatch Packet</b> — perfect for site engineers.</div>
            <button
              data-testid="btn-download-packet"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("srad_token");
                  const resp = await fetch(`${API}/dispatches/${id}/packet`, {
                    credentials: "include",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  });
                  if (!resp.ok) {
                    const t = await resp.text();
                    toast.error(t.slice(0, 120) || "Packet download failed"); return;
                  }
                  const blob = await resp.blob();
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `${d.dispatch_code}-packet.pdf`;
                  link.click();
                } catch (e) { toast.error("Packet download failed"); }
              }}
              className="w-full bg-[var(--amber)] hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-md"
            >Download Dispatch Packet</button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold">Documents</h2>
          <div className="text-xs text-slate-500">{documents.length} active · Delete leaves the slot open for re-upload</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {(() => {
            const CORE_TYPES = ["Tax Invoice", "LR / Lorry Receipt", "Client PO", "POD", "Packing List", "E-Way Bill", "Delivery Challan", "Test Certificate", "Other"];
            // group active docs by type; keep every doc (including duplicates)
            const byType = {};
            documents.forEach((doc) => {
              (byType[doc.doc_type] = byType[doc.doc_type] || []).push(doc);
            });
            const cards = [];
            CORE_TYPES.forEach((type) => {
              const items = byType[type] || [];
              if (items.length === 0) {
                // Empty slot — always show upload option, never hide
                cards.push(
                  canEdit ? (
                    <label
                      key={`slot-${type}`}
                      data-testid={`slot-empty-${type.replace(/\s+/g, "-")}`}
                      className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50 rounded-md p-3 flex flex-col items-center justify-center text-center min-h-[92px]"
                    >
                      <Upload size={16} className="text-slate-400 mb-1" />
                      <div className="text-xs font-semibold text-slate-700 uppercase">{type}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Upload {type === "POD" ? "POD" : "PDF"}</div>
                      <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) { uploadDocForSlot(type, e.target.files[0]); e.target.value = ""; } }} />
                    </label>
                  ) : (
                    <div key={`slot-${type}`} className="border border-dashed border-slate-200 rounded-md p-3 text-xs text-slate-400 uppercase min-h-[92px] flex items-center justify-center">
                      {type} — not uploaded
                    </div>
                  )
                );
              } else {
                items.forEach((doc) => {
                  cards.push(
                    <div key={doc.id} data-testid={`doc-card-${doc.id}`} className="group relative border border-slate-200 rounded-md p-3 hover:border-slate-300 transition min-h-[92px]">
                      <a href={`${API}/documents/${doc.id}/download`} target="_blank" rel="noreferrer" className="block">
                        <div className="text-xs font-semibold text-emerald-700 uppercase">{doc.doc_type}</div>
                        <div className="text-sm font-medium text-slate-800 truncate mt-1">{doc.original_filename}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{Math.round(doc.size / 1024)} KB · {new Date(doc.uploaded_at).toLocaleDateString()}{doc.replaced_at ? " · replaced" : ""}</div>
                      </a>
                      {canEdit && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          <label
                            data-testid={`btn-replace-doc-${doc.id}`}
                            title="Replace file"
                            className="cursor-pointer bg-white border border-slate-300 hover:bg-sky-50 rounded p-1 text-sky-700"
                          >
                            <RefreshCw size={13} />
                            <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) { replaceDoc(doc, e.target.files[0]); e.target.value = ""; } }} />
                          </label>
                          <button
                            data-testid={`btn-delete-doc-${doc.id}`}
                            title="Remove — slot stays open for re-upload"
                            onClick={(e) => { e.preventDefault(); deleteDoc(doc); }}
                            className="bg-white border border-slate-300 hover:bg-rose-50 rounded p-1 text-rose-700"
                          ><Trash2 size={13} /></button>
                        </div>
                      )}
                    </div>
                  );
                });
              }
            });
            return cards;
          })()}
        </div>
      </div>

      {!isClient && (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="font-heading text-lg font-bold mb-3">Communication Log</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
                <th className="py-2">Channel</th><th className="py-2">Recipient</th><th className="py-2">To</th>
                <th className="py-2">Sent</th><th className="py-2">Status</th><th className="py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {comms.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="py-2"><span className="text-xs font-semibold uppercase px-2 py-0.5 rounded-full bg-slate-100">{c.channel}</span></td>
                  <td className="py-2">{c.contact_name}</td>
                  <td className="py-2 font-mono text-xs">{c.to_address}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(c.sent_at).toLocaleString()}</td>
                  <td className="py-2"><span className={`text-xs font-semibold ${c.status === "sent" ? "text-emerald-700" : "text-rose-700"}`}>{c.status}</span></td>
                  <td className="py-2 text-xs text-slate-500 max-w-xs truncate">{c.error || "-"}</td>
                </tr>
              ))}
              {comms.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-slate-500 text-sm">No notifications sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {resendOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setResendOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-resend">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Send size={18} /> Resend Dispatch Notifications</h2>
            <div className="text-xs text-slate-500 mt-1">For <span className="font-mono">{d.dispatch_code}</span> · Invoice {d.invoice_number} · LR {d.lr_number}</div>

            {lastEmail && (
              <div data-testid="resend-duplicate-warning" className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <div className="font-semibold">Already sent</div>
                <div className="text-xs mt-0.5">
                  Last email sent on {new Date(lastEmail.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} to {lastEmail.recipient_count || (lastEmail.to_list ? lastEmail.to_list.length : 1)} recipient(s). Please confirm before resending.
                </div>
              </div>
            )}

            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              One consolidated email is sent to all selected contacts (using To + persistent CC). WhatsApp is still delivered per-contact. Invoice, LR and POD documents (if available) are auto-attached.
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase font-semibold tracking-widest text-slate-500 mb-2">Recipients</div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button type="button" onClick={() => { const s = {}; contacts.forEach((c) => s[c.id] = true); setSelContacts(s); }} className="text-xs px-3 py-1 border border-slate-300 rounded-full">Select All</button>
                <button type="button" onClick={() => setSelContacts({})} className="text-xs px-3 py-1 border border-slate-300 rounded-full">Clear</button>
                <div className="ml-auto flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={selChannels.whatsapp} onChange={(e) => setSelChannels({ ...selChannels, whatsapp: e.target.checked })} data-testid="resend-ch-whatsapp" /> WhatsApp</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={selChannels.email} onChange={(e) => setSelChannels({ ...selChannels, email: e.target.checked })} data-testid="resend-ch-email" /> Email</label>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto">
                {contacts.map((c) => (
                  <label key={c.id} data-testid={`resend-contact-${c.id}`} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={!!selContacts[c.id]} onChange={(e) => setSelContacts({ ...selContacts, [c.id]: e.target.checked })} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{c.name} <span className="text-xs text-slate-500">· {c.department}</span></div>
                      <div className="text-xs text-slate-500">{c.whatsapp && <span>WA: {c.whatsapp}</span>} {c.email && <span className="ml-3">Email: {c.email}</span>}</div>
                    </div>
                    <span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{c.preferred}</span>
                  </label>
                ))}
                {contacts.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">No contacts for this client.</div>}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase font-semibold tracking-widest text-slate-500 mb-2">Attach documents</div>
              <div className="grid grid-cols-2 gap-2">
                {documents.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <input type="checkbox" checked={!!selDocs[doc.id]} onChange={(e) => setSelDocs({ ...selDocs, [doc.id]: e.target.checked })} />
                    <span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 font-semibold">{doc.doc_type}</span>
                    <span className="text-slate-500 truncate">{doc.original_filename}</span>
                  </label>
                ))}
                {documents.length === 0 && <div className="col-span-2 text-sm text-slate-500">No documents uploaded to this dispatch.</div>}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setResendOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button
                data-testid="btn-do-resend"
                disabled={resending}
                onClick={doResend}
                className="px-4 py-2 text-sm bg-[var(--amber)] hover:bg-amber-600 text-white rounded-md font-semibold flex items-center gap-2 disabled:opacity-60"
              >
                <Send size={13} /> {resending ? "Resending…" : "Resend Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {queryOpen && (        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setQueryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="dialog-query">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><MessageSquare size={18} /> Raise a Query</h2>
            <div className="text-xs text-slate-500 mt-1">Against dispatch <span className="font-mono">{d.dispatch_code}</span></div>
            <div className="space-y-3 mt-4">
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Category</label>
                <select data-testid="query-category" value={qForm.category} onChange={(e) => setQForm({ ...qForm, category: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                  <option value="dispatch">Dispatch related</option>
                  <option value="pod">Pending POD / Delivery Proof</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Subject</label>
                <input data-testid="query-subject" value={qForm.subject} onChange={(e) => setQForm({ ...qForm, subject: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="Short summary of your question" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Message</label>
                <textarea data-testid="query-message" rows={5} value={qForm.message} onChange={(e) => setQForm({ ...qForm, message: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="Describe your question in detail…" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setQueryOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-submit-query" onClick={submitQuery} className="px-4 py-2 text-sm bg-[var(--amber)] hover:bg-amber-600 text-white rounded-md">Submit Query</button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setEditOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-edit-dispatch">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Pencil size={18} /> Edit Dispatch</h2>
                <div className="text-xs text-slate-500 mt-1">Correct any wrongly entered field. Every change is captured in the audit history.</div>
              </div>
              <button onClick={() => setEditOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              {[
                ["invoice_number", "Invoice Number", "text"],
                ["invoice_date", "Invoice Date", "date"],
                ["invoice_amount", "Invoice Amount (Rs.)", "number"],
                ["client_po_number", "Client PO Number", "text"],
                ["po_date", "PO Date", "date"],
                ["delivery_location", "Delivery Location", "text"],
                ["lr_number", "LR / Docket Number", "text"],
                ["dispatch_date", "Dispatch Date", "date"],
                ["expected_delivery", "Expected Delivery", "date"],
                ["transport_amount", "Transport Amount (Rs.)", "number"],
                ["loading_cost", "Loading Cost", "number"],
                ["unloading_cost", "Unloading Cost", "number"],
                ["packing_cost", "Packing Cost", "number"],
                ["insurance_cost", "Insurance Cost", "number"],
                ["other_charges", "Other Charges", "number"],
              ].map(([k, l, type]) => (
                <div key={k}>
                  <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{l}</label>
                  <input
                    data-testid={`edit-${k}`}
                    type={type}
                    value={editForm[k] ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Transporter</label>
                <select
                  data-testid="edit-transporter_id"
                  value={editForm.transporter_id || ""}
                  onChange={(e) => setEditForm({ ...editForm, transporter_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  {transporters.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Remarks</label>
                <textarea
                  data-testid="edit-remarks"
                  rows={2}
                  value={editForm.remarks ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-save-edit" disabled={savingEdit} onClick={saveEdit} className="px-4 py-2 text-sm bg-[var(--navy)] text-white rounded-md disabled:opacity-60">{savingEdit ? "Saving…" : "Save Corrections"}</button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setHistoryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-history">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><History size={18} /> Audit History</h2>
                <div className="text-xs text-slate-500 mt-1">Every edit, replace and delete against this dispatch & its documents.</div>
              </div>
              <button onClick={() => setHistoryOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            {historyLoading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
            {!historyLoading && historyLogs.length === 0 && (
              <div className="mt-6 text-sm text-slate-500">No changes recorded yet.</div>
            )}
            {!historyLoading && historyLogs.length > 0 && (
              <div className="mt-4 border border-slate-200 rounded-md divide-y divide-slate-100">
                {historyLogs.map((l) => (
                  <div key={l.id} data-testid={`history-row-${l.id}`} className="p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-widest bg-slate-100 rounded px-2 py-0.5 text-slate-700">{l.action}</span>
                        <span className="text-xs text-slate-500">{l.entity}</span>
                      </div>
                      <div className="text-xs text-slate-500">{new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      by <b>{l.actor_name}</b> {l.actor_email && <span className="text-slate-400">· {l.actor_email}</span>}
                    </div>
                    {l.details?.changes && Object.keys(l.details.changes).length > 0 && (
                      <div className="mt-2 text-xs bg-slate-50 rounded p-2 space-y-1">
                        {Object.entries(l.details.changes).map(([k, v]) => (
                          <div key={k} className="grid grid-cols-3 gap-2">
                            <span className="font-mono text-slate-500">{k}</span>
                            <span className="line-through text-rose-600 truncate">{String(v.from ?? "—")}</span>
                            <span className="text-emerald-700 font-semibold truncate">{String(v.to ?? "—")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {l.details && !l.details.changes && Object.keys(l.details).length > 0 && (
                      <div className="mt-1 text-xs text-slate-500 truncate">{JSON.stringify(l.details)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="font-heading text-xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
